import { supabase } from "@/integrations/supabase/client";
import { getUserFriendlyMessage } from "./errorMessages";

/**
 * Generates a short human-readable error code, e.g. "ERR-A3F9-2K7Q".
 * ~16^4 * 32^2 = ~67M combinations, collision-safe with UNIQUE constraint.
 */
export function generateErrorCode(): string {
  const hex = Math.floor(Math.random() * 0xffff).toString(16).toUpperCase().padStart(4, "0");
  const base32chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no confusing chars
  let suffix = "";
  for (let i = 0; i < 4; i++) {
    suffix += base32chars[Math.floor(Math.random() * base32chars.length)];
  }
  return `ERR-${hex}-${suffix}`;
}

export type ReportedError = {
  code: string;
  message: string;
  userMessage: string;
  hint?: string;
};

export type ErrorContext = {
  source?: "frontend" | "edge_function" | "stripe_webhook" | "other";
  route?: string;
  extra?: Record<string, unknown>;
};

/**
 * Logs an error to the `error_reports` table and returns a code for the UI.
 * Never throws — logging must never break the app flow.
 */
export async function reportError(
  error: unknown,
  context: ErrorContext = {}
): Promise<ReportedError> {
  const err = normalizeError(error);
  const code = generateErrorCode();
  const friendly = getUserFriendlyMessage(err.message);

  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id ?? null;

    await supabase.from("error_reports").insert({
      code,
      user_id: userId,
      route: context.route ?? (typeof window !== "undefined" ? window.location.pathname : null),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      error_message: err.message.slice(0, 2000),
      error_stack: err.stack?.slice(0, 8000) ?? null,
      context: (context.extra ?? {}) as any,
      source: context.source ?? "frontend",
    });
  } catch (loggingErr) {
    // Never let logging break the app
    console.error("[errorReporting] Failed to log error", loggingErr);
  }

  // Also log to console for developer visibility
  console.error(`[${code}]`, err);

  return {
    code,
    message: err.message,
    userMessage: friendly.message,
    hint: friendly.hint,
  };
}

function normalizeError(e: unknown): { message: string; stack?: string } {
  if (e instanceof Error) return { message: e.message, stack: e.stack };
  if (typeof e === "string") return { message: e };
  if (e && typeof e === "object") {
    const anyE = e as any;
    return {
      message: anyE.message ?? anyE.error ?? JSON.stringify(e).slice(0, 500),
      stack: anyE.stack,
    };
  }
  return { message: String(e) };
}

/**
 * Global handlers — installed once at app startup.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    void reportError(event.error ?? event.message, { source: "frontend", extra: { type: "window.error" } });
  });
  window.addEventListener("unhandledrejection", (event) => {
    void reportError(event.reason, { source: "frontend", extra: { type: "unhandledrejection" } });
  });
}
